/* Shared by the API, signing pad and both proof overlays. No artwork is modified. */

(()=>{'use strict';

const colors=[{value:'#121513',name:'Black'},{value:'#ffffff',name:'White'},{value:'#c0c0c0',name:'Silver'}];

function rgb(hex){return /^#[0-9a-f]{6}$/i.test(hex||'')?[1,3,5].map(i=>parseInt(hex.slice(i,i+2),16)):null;}

function luminance(c){return c.map(v=>{v/=255;return v<=.04045?v/12.92:((v+.055)/1.055)**2.4;}).reduce((n,v,i)=>n+v*[.2126,.7152,.0722][i],0);}

function contrast(a,b){const x=luminance(a),y=luminance(b);return (Math.max(x,y)+.05)/(Math.min(x,y)+.05);}

function preference(palette){const result=[];for(const hex of palette||[]){const c=rgb(hex);if(!c||Math.max(...c)-Math.min(...c)>22)continue;const mean=c.reduce((a,b)=>a+b,0)/3,value=mean<65?'#121513':mean>235?'#ffffff':mean>=130?'#c0c0c0':null;if(value&&!result.includes(value))result.push(value);}return result;}

function calibration(area){

 const background=area?.background;

 if(!Array.isArray(background)||background.length!==3||background.some(v=>!Number.isFinite(v)||v<0||v>255))return null;

 const options=colors.map(c=>({...c,contrast:contrast(rgb(c.value),background)})),best=Math.max(...options.map(c=>c.contrast));

 // 4.5:1 where possible. On a midtone use the highest-contrast ink.

 const threshold=Math.min(4.5,best),allowed=options.filter(c=>c.contrast+1e-9>=threshold),preferences=preference(area.inkPreferences);

 const recommended=preferences.map(value=>allowed.find(c=>c.value===value)).find(Boolean)||[...allowed].sort((a,b)=>b.contrast-a.contrast)[0];

 return {background,css:'rgb('+background.join(',')+')',recommended:recommended.value,options:options.map(c=>({...c,allowed:allowed.includes(c)})),preferences,threshold};

}

function resolve(value,area){const c=calibration(area);return c?(c.options.find(o=>o.value===value&&o.allowed)?.value||c.recommended):value;}

globalThis.GamedayInk={colors,rgb,contrast,calibration,resolve,preference};

})();



/* Visitor ink only. Original product image pixels are never rewritten. */

(()=>{'use strict';

function hash(text){let n=2166136261;for(const c of String(text)){n^=c.charCodeAt(0);n=Math.imul(n,16777619);}return n>>>0;}

function random(seed){let n=hash(seed);return()=>{n+=0x6D2B79F5;let t=n;t=Math.imul(t^t>>>15,t|1);t^=t+Math.imul(t^t>>>7,t|61);return((t^t>>>14)>>>0)/4294967296;};}

function bounds(strokes){let x=1,y=1,r=0,b=0;for(const s of strokes)for(const p of s){x=Math.min(x,p[0]);y=Math.min(y,p[1]);r=Math.max(r,p[0]);b=Math.max(b,p[1]);}return{x,y,w:Math.max(.015,r-x),h:Math.max(.015,b-y)};}

function sides(entries){const sorted=[...entries].sort((a,b)=>String(a.signedAt||'~').localeCompare(String(b.signedAt||'~'))||String(a.id).localeCompare(String(b.id)));return [sorted.filter((e,i)=>e.side?e.side==='front':i%2===0),sorted.filter((e,i)=>e.side?e.side==='back':i%2===1)];}

const policy={maxWidth:.15,maxHeight:.085,displayLimit:24,mobileLimit:12};

function valid(area){return area&&['field','mark'].every(k=>{const r=area[k];return r&&['left','top','right','bottom'].every(v=>Number.isFinite(r[v])&&r[v]>=0&&r[v]<=1)&&r.left<r.right&&r.top<r.bottom;})&&area.mark.left>=area.field.left&&area.mark.right<=area.field.right&&area.mark.top>=area.field.top&&area.mark.bottom<=area.field.bottom;}

function outsideMark(p,w,h,area){if(!valid(area))return false;const m=area.mark;return p.x+p.dx<=m.left*w||p.x-p.dx>=m.right*w||p.y+p.dy<=m.top*h||p.y-p.dy>=m.bottom*h;}

function overlap(a,b){const x=Math.max(0,Math.min(a.x+a.dx,b.x+b.dx)-Math.max(a.x-a.dx,b.x-b.dx)),y=Math.max(0,Math.min(a.y+a.dy,b.y+b.dy)-Math.max(a.y-a.dy,b.y-b.dy));return x*y/Math.max(1e-9,Math.min(a.dx*a.dy,b.dx*b.dy)*4);}

function layout(entries,width,height,seed='flag',area){

 if(!valid(area)||width<=0||height<=0)return[];

 const list=[...entries].sort((a,b)=>String(a.signedAt||'~').localeCompare(String(b.signedAt||'~'))||String(a.id).localeCompare(String(b.id))),f=area.field,m=area.mark,placed=[];

 const regions=[[f.left,f.top,m.left,f.bottom],[m.right,f.top,f.right,f.bottom],[m.left,f.top,m.right,m.top],[m.left,m.bottom,m.right,f.bottom]].map(([l,t,r,b])=>({x:l*width,y:t*height,w:(r-l)*width,h:(b-t)*height})).filter(r=>r.w>0&&r.h>0);

 if(!regions.length)return[];

 for(const entry of list){const r=random(seed+entry.id),b=bounds(entry.strokes),aspect=Math.max(.25,Math.min(20,2.5*b.w/b.h)),nominal=width*(.12+r()*.03);let best=null,score=Infinity;

  // Continuous random coordinates and angles, never rows or cells. Keep the marker readable.

  for(let round=0;round<4&&score>.02;round++)for(let attempt=0;attempt<360;attempt++){

   const region=regions[Math.floor(r()*regions.length)],angle=(r()*90-45)*Math.PI/180,cos=Math.abs(Math.cos(angle)),sin=Math.abs(Math.sin(angle));

   let w=nominal*Math.pow(.9,round),h=w/aspect;const cap=Math.min(1,height*policy.maxHeight/h);w*=cap;h*=cap;

   const fit=Math.min(1,region.w*.92/(w*cos+h*sin+w*.028),region.h*.92/(w*sin+h*cos+w*.028));if(fit<.68)continue;w*=fit;h*=fit;

   const stroke=w*.025,dx=(w*cos+h*sin+stroke)/2,dy=(w*sin+h*cos+stroke)/2;

   const x=region.x+dx+r()*(region.w-2*dx),y=region.y+dy+r()*(region.h-2*dy),p={entry,b,angle,width:w,height:h,x,y,dx,dy,stroke,aroundMark:true};

   const crowd=placed.reduce((n,v)=>n+overlap(p,v),0);if(crowd<score){best=p;score=crowd;}if(score<=.02)break;

  }

  // Narrow protected borders still need to display a legitimate autograph.

  // Keep its aspect ratio and choose the largest readable fit outside the mark.

  if(!best){

   let quality=-Infinity;

   for(let attempt=0;attempt<720;attempt++){

    const region=regions[attempt%regions.length],angle=(r()*90-45)*Math.PI/180,cos=Math.abs(Math.cos(angle)),sin=Math.abs(Math.sin(angle));

    let w=nominal,h=w/aspect;const cap=Math.min(1,height*policy.maxHeight/h);w*=cap;h*=cap;

    const fit=Math.min(1,region.w*.92/(w*cos+h*sin+w*.028),region.h*.92/(w*sin+h*cos+w*.028));w*=fit;h*=fit;

    if(w<=0||h<=0)continue;

    const stroke=w*.025,dx=(w*cos+h*sin+stroke)/2,dy=(w*sin+h*cos+stroke)/2;

    const x=region.x+dx+r()*(region.w-2*dx),y=region.y+dy+r()*(region.h-2*dy),p={entry,b,angle,width:w,height:h,x,y,dx,dy,stroke,aroundMark:true};

    const crowd=placed.reduce((n,v)=>n+overlap(p,v),0),value=w/(1+crowd*4);

    if(outsideMark(p,width,height,area)&&value>quality){best=p;quality=value;}

   }

  }

  if(best)placed.push(best);

 }

 return placed;

}

// A round felt nib with modest directional width variation. No glow or decorative shadow.

function marker(ctx,paths,x,y,w,h,color,weight,progress=1){

 const segments=[];let total=0;

 for(const s of paths){for(let i=1;i<s.length;i++){const a=[x+s[i-1][0]*w,y+s[i-1][1]*h],b=[x+s[i][0]*w,y+s[i][1]*h],length=Math.hypot(b[0]-a[0],b[1]-a[1]);segments.push({a,b,length});total+=length;}if(s.length){segments.push({lift:true,length:weight*6});total+=weight*6;}}

 let remaining=total*Math.max(0,Math.min(1,progress)),tip=null;ctx.save();ctx.strokeStyle=color;ctx.lineCap='round';ctx.lineJoin='round';

 for(const s of segments){if(remaining<=0)break;if(s.lift){remaining-=s.length;continue;}const fraction=Math.min(1,remaining/Math.max(s.length,.001)),b=[s.a[0]+(s.b[0]-s.a[0])*fraction,s.a[1]+(s.b[1]-s.a[1])*fraction],angle=Math.atan2(s.b[1]-s.a[1],s.b[0]-s.a[0]);ctx.lineWidth=weight*(.83+.17*Math.abs(Math.sin(angle+Math.PI/4)));ctx.beginPath();ctx.moveTo(...s.a);ctx.lineTo(...b);ctx.stroke();tip={x:b[0],y:b[1]};remaining-=s.length;}

 ctx.restore();return tip;

}

function ink(ctx,p,progress=1){const paths=p.entry.strokes.map(s=>s.map(v=>[(v[0]-p.b.x)/p.b.w,(v[1]-p.b.y)/p.b.h]));ctx.save();ctx.translate(p.x,p.y);ctx.rotate(p.angle);marker(ctx,paths,-p.width/2,-p.height/2,p.width,p.height,p.entry.ink,p.stroke,progress);ctx.restore();}

const animations=new WeakMap();

function paint(canvas,entries,seed,visible=true,area,animation){

 const previous=animations.get(canvas);if(previous)cancelAnimationFrame(previous);

 const rect=canvas.getBoundingClientRect(),w=Math.max(1,rect.width),h=Math.max(1,rect.height),dpr=Math.min(2,globalThis.devicePixelRatio||1);

 canvas.width=Math.round(w*dpr);canvas.height=Math.round(h*dpr);const c=canvas.getContext('2d');c.scale(dpr,dpr);if(!visible)return[];

 const placed=layout(entries.map(e=>({...e,ink:globalThis.GamedayInk?.resolve(e.ink,area)||e.ink})),w,h,seed,area),moving=placed.find(p=>p.entry.id===animation?.id),start=performance.now(),duration=Math.max(0,Math.min(3200,animation?.duration||0));

 function frame(now){c.clearRect(0,0,w,h);const progress=duration?Math.min(1,(now-start)/duration):1;for(const p of placed)ink(c,p,p===moving?progress:1);if(moving&&progress<1)animations.set(canvas,requestAnimationFrame(frame));else animations.delete(canvas);}

 frame(start);return placed;

}

let momentDialog,momentFrame=0;

function celebrate(entry,{title='Gameday Flags',onDone,saved=true}={}){

 if(momentDialog?.open)momentDialog.close();

 if(!document.getElementById('gameday-moment-style')){const style=document.createElement('style');style.id='gameday-moment-style';style.textContent=`.gameday-moment{box-sizing:border-box;width:min(760px,94vw);max-height:92dvh;overflow:auto;padding:0;border:1px solid #56603c;border-radius:14px;background:#141915;color:#f7f8ed;font:15px/1.5 Arial,sans-serif;box-shadow:0 22px 100px #0009}.gameday-moment::backdrop{background:#040704d9;backdrop-filter:blur(5px)}.gameday-moment header{padding:28px 30px 18px}.gameday-moment .mvp-label{font-size:11px;letter-spacing:2px;font-weight:800;color:#c7e842}.gameday-moment h2{font-size:clamp(30px,5vw,46px);line-height:1.08;margin:10px 0}.gameday-moment .moment-team{color:#b8c4b0;margin:0}.gameday-moment .moment-stage{position:relative;margin:0 24px;border:1px solid #45503e;border-radius:8px;overflow:hidden;background:#0b1c3a}.gameday-moment canvas{width:100%;height:260px;display:block}.gameday-moment .moment-ink-label{position:absolute;left:18px;bottom:12px;font-size:10px;letter-spacing:1.5px;opacity:.6}.gameday-moment footer{padding:18px 30px 26px}.gameday-moment .moment-state{font-size:13px;color:#c7d0bd;min-height:40px}.gameday-moment .moment-actions{display:flex;flex-wrap:wrap;gap:10px}.gameday-moment button{border:0;border-radius:4px;padding:12px 16px;font:bold 13px Arial;cursor:pointer;background:#c7e842;color:#16200e}.gameday-moment button.secondary{background:#303a2c;color:#e8eee0}.gameday-moment button:focus-visible{outline:2px solid white;outline-offset:3px}@media(max-width:500px){.gameday-moment header{padding:22px 20px 14px}.gameday-moment .moment-stage{margin:0 14px}.gameday-moment canvas{height:200px}.gameday-moment footer{padding:14px 20px 22px}}`;document.head.append(style);}

 const dialog=document.createElement('dialog');dialog.className='gameday-moment';dialog.setAttribute('aria-labelledby','gameday-moment-title');dialog.innerHTML='<header><div class="mvp-label">MVP / THE EARLY CROWD</div><h2 id="gameday-moment-title">That’s your mark.</h2><p class="moment-team"></p></header><div class="moment-stage"><canvas aria-label="Your handwritten autograph replay"></canvas><span class="moment-ink-label">YOUR AUTOGRAPH / ORIGINAL PEN STROKES</span></div><footer><p class="moment-state" role="status"></p><div class="moment-actions"><button type="button" class="moment-done">Back to the flag</button><button type="button" class="secondary moment-replay">Replay my autograph</button></div></footer>';

 dialog.querySelector('.moment-team').textContent=title;const light=entry.ink==='#121513';dialog.querySelector('.moment-stage').style.background=light?'#f1f0e5':'#0b1c3a';dialog.querySelector('.moment-stage').style.color=light?'#182014':'#f7f8ed';document.body.append(dialog);momentDialog=dialog;

 let oldFocus=document.activeElement;

 function replay(){cancelAnimationFrame(momentFrame);const canvas=dialog.querySelector('canvas'),rect=canvas.getBoundingClientRect(),dpr=Math.min(2,devicePixelRatio||1);canvas.width=Math.round(rect.width*dpr);canvas.height=Math.round(rect.height*dpr);const ctx=canvas.getContext('2d');ctx.scale(dpr,dpr);const b=bounds(entry.strokes),aspect=2.5*b.w/b.h,w=Math.min(rect.width*.78,rect.height*.58*aspect),h=w/aspect,x=(rect.width-w)/2,y=(rect.height-h)/2,paths=entry.strokes.map(s=>s.map(p=>[(p[0]-b.x)/b.w,(p[1]-b.y)/b.h])),duration=matchMedia('(prefers-reduced-motion: reduce)').matches?0:2600,start=performance.now(),status=dialog.querySelector('.moment-state');status.textContent='Your moment. Your handwriting.';dialog.dataset.animating=String(duration>0);

  function frame(now){const progress=duration?Math.min(1,(now-start)/duration):1;ctx.clearRect(0,0,rect.width,rect.height);const tip=marker(ctx,paths,x,y,w,h,entry.ink,Math.max(2.8,w*.011),progress);

   if(tip&&progress<1){ctx.save();ctx.translate(tip.x,tip.y);ctx.rotate(.62);const scale=Math.min(1,rect.width/600);ctx.scale(scale,scale);ctx.fillStyle='#111412';ctx.strokeStyle='#77806f';ctx.lineWidth=1;ctx.beginPath();ctx.roundRect(-8,-106,16,94,5);ctx.fill();ctx.stroke();ctx.fillStyle='#b8bdb1';ctx.fillRect(-7,-29,14,8);ctx.fillStyle=entry.ink;ctx.beginPath();ctx.moveTo(-4,-13);ctx.lineTo(4,-13);ctx.lineTo(1,0);ctx.lineTo(-1,0);ctx.closePath();ctx.fill();ctx.restore();}

   if(progress<1&&dialog.open)momentFrame=requestAnimationFrame(frame);else{dialog.dataset.animating='false';status.textContent=saved?'Your autograph has been saved. Thanks for signing the flag.':'Preview only. No autograph has been submitted.';}

  }frame(start);

 }

 dialog.querySelector('.moment-done').onclick=()=>dialog.close();dialog.querySelector('.moment-replay').onclick=replay;dialog.addEventListener('close',()=>{cancelAnimationFrame(momentFrame);dialog.remove();if(momentDialog===dialog)momentDialog=null;oldFocus?.focus();onDone?.();},{once:true});dialog.showModal();replay();

}

globalThis.GamedayCollage={layout,paint,sides,hash,policy,outsideMark,valid,bounds,marker,overlap,celebrate};

})();